import { InfoOutlined } from "@mui/icons-material";
import {
  FormControl,
  FormHelperText,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box, styled } from "@mui/system";
import { TableUpdateStatusCell } from "@pimo/pimo-components";
import { LockDetail } from "@pimo/resource-lock-plugin";
import {
  ImpactSeries,
  Initiative,
  InitiativeImpactSeries,
  MonthlyImpactMap,
  User,
} from "aint-types";
import {
  formatDate,
  formatDateUTC,
  getImpactRangeForDates,
  getUpdateChipColorBasedOnUpdateStatus,
  IMPACT_SERIES_IDS_AND_LABELS,
  REPORTING_IMPACT_CURRENTLY_LOCKED_TOOLTIP,
  sortInitiatives,
} from "aint-utils";
import dayjs from "dayjs";
import { FC, useState } from "react";
import { FieldErrors, FieldValues, useFormContext } from "react-hook-form";
import { Form, generatePath, Link } from "react-router-dom";

import {
  ImpactPlanSection,
  TieAndHrCostImpactSection,
} from "../../../components/impact-plan-section/impact-plan-section";
import { UpdateIcon } from "../../../components/update-icon/update-icon";
import { APP_ROUTES } from "../../constants";
import { getConfirmationTypesForImpactRange } from "../../utils/get-confirmation-types-for-impact-range";

const Headline = styled("h1")(() => ({
  fontWeight: 700,
  fontSize: "16px",
}));

export interface UpdateImpactDialogContentProps {
  /** submit handler */
  onSubmit: (initiative: FieldValues) => void;
  /** invalid handler */
  onInvalid: (errors: FieldErrors) => void;
  /** The active/currently open initiative to be highlighted in the navigation. */
  activeInitiative: Initiative;
  /** The initiatives to be shown in the navigation. */
  initiatives: Initiative[];
  /** Name of the form */
  formName: string;
  /** List of impact series which need their forecast confirmed */
  initiativesLockDetails: Record<number, LockDetail>;
  currentUser: User;
  switchReportingLock: (
    oldInitiativeId: number,
    newInitiativeId: number
  ) => void;
}

/** contents of the edit dialog,  */
export const UpdateImpactDialogContent: FC<UpdateImpactDialogContentProps> = ({
  formName,
  onSubmit,
  onInvalid,
  activeInitiative,
  initiatives,
  initiativesLockDetails,
  currentUser,
  switchReportingLock,
}: UpdateImpactDialogContentProps) => {
  const { handleSubmit } = useFormContext();

  return (
    <Box
      sx={{
        margin: 0,
        height: "100%",
      }}
    >
      <Box display="flex" flexDirection="row" height="100%">
        <Navigation
          initiatives={initiatives}
          activeInitiative={activeInitiative}
          initiativesLockDetails={initiativesLockDetails}
          currentUser={currentUser}
          switchReportingLock={switchReportingLock}
        />
        <Box
          sx={{
            overflowY: "scroll",
          }}
        >
          <Form id={formName} onSubmit={handleSubmit(onSubmit, onInvalid)}>
            <Content initiative={activeInitiative} />
          </Form>
        </Box>
      </Box>
    </Box>
  );
};

function Content({
  initiative: {
    initiativeName,
    startDate,
    monitoringEndDate,
    applicableImpactSeries,
    updateStatus,
    dueDate,
    uid,
  },
}: {
  initiative: Initiative;
}) {
  const {
    watch,
    getValues,
    formState: { errors },
  } = useFormContext();
  const [tieIncludesHrCosts, actualConfirmation] = watch([
    "tieServiceExpensesIncludesHrCosts",
    "actualConfirmation",
  ]);

  const startYear = dayjs(startDate).year();
  const endYear = dayjs(monitoringEndDate).year();
  const years: number[] = [];

  for (let year = startYear; year <= endYear; year++) {
    years.push(year);
  }

  const currentYear = dayjs().year();
  const [selectedYear, setSelectedYear] = useState(
    years.includes(currentYear) ? currentYear : years[years.length - 1]
  );

  const impactRange =
    !startDate || !monitoringEndDate
      ? []
      : getImpactRangeForDates(
          formatDateUTC(startDate, "", "dayjs") as dayjs.Dayjs,
          dayjs(monitoringEndDate)
        );

  return (
    <Grid
      container
      gap={1}
      flexDirection="column"
      sx={(theme) => ({
        background: "white",
        padding: theme.spacing(4),
      })}
    >
      <Box display="flex" flexDirection="row" alignItems="center" gap="24px">
        <Headline>{initiativeName}</Headline>
        <TableUpdateStatusCell
          cardProps={{
            sx: {
              div: {
                div: {
                  lineHeight: "initial",
                  height: "initial",
                },
              },
            },
          }}
          icon={
            getUpdateChipColorBasedOnUpdateStatus(updateStatus ?? "completed")
              .icon
          }
          chipColor={
            getUpdateChipColorBasedOnUpdateStatus(updateStatus ?? "completed")
              .color
          }
          body={`Due: ${formatDate(dueDate, "YYYY/MM/DD")}`}
        />
      </Box>
      <YearSelect
        selectedYear={selectedYear}
        years={years}
        setSelectedYear={(event: SelectChangeEvent) => {
          setSelectedYear(Number.parseInt(event.target.value));
        }}
        hasError={Object.keys(errors).length > 0}
      />
      {(applicableImpactSeries ?? []).map((seriesId) => {
        const confirmationTypes = getConfirmationTypesForImpactRange({
          confirmationDate: actualConfirmation
            ? dayjs(actualConfirmation[seriesId as ImpactSeries])
            : undefined,
          impactRange,
          startDate: startDate ? dayjs(startDate) : undefined,
          impactSeries: getValues()[
            (seriesId + "Actual") as keyof InitiativeImpactSeries
          ] as MonthlyImpactMap,
        });

        const error = errors[seriesId + "Actual"];
        const months =
          impactRange.find(
            (yearAndMonth) => yearAndMonth.year === selectedYear.toString()
          )?.months ?? [];

        const ImpactPlanElement =
          seriesId === "tieServiceExpensesImpact" && tieIncludesHrCosts
            ? TieAndHrCostImpactSection
            : ImpactPlanSection;

        // TODO: React hook form doesnt like "changing" inputs. So the input values arent filled in correctly if the year changes.
        // It works if all inputs are always rendered and only the correct year are shown, could be a performance issue tho.
        // return (
        //   <ImpactPlanSection
        //     year={selectedYear.toString()}
        //     months={months}
        //     seriesId={series}
        //     seriesLabel={`${SERIES_IDS_AND_LABELS.find((s) => s.id === series)?.label} ${selectedYear}`}
        //     rows={2}
        //     isReportingUpdate={true}
        //   />
        // );
        return (
          <>
            {years.map((year) => {
              const seriesLabel = IMPACT_SERIES_IDS_AND_LABELS.find(
                (s) => s.id === seriesId
              )?.label;
              return (
                <Box
                  key={year}
                  display={year === selectedYear ? "block" : "none"}
                >
                  {error && (
                    <FormHelperText
                      sx={(theme) => ({ color: theme.palette.error.main })}
                    >
                      {error.message as string}
                    </FormHelperText>
                  )}
                  <ImpactPlanElement
                    activeInitiativeUID={uid}
                    year={year.toString()}
                    months={months}
                    seriesId={seriesId}
                    seriesLabel={`${seriesLabel} ${year} ${seriesId.includes("Fte") ? "(in # FTE)" : "(in k EUR)"}`}
                    isReportingUpdate={true}
                    twoRowBreakpoint="(min-width: 1250px)"
                    confirmationTypes={confirmationTypes}
                  />
                </Box>
              );
            })}
          </>
        );
      })}
    </Grid>
  );
}

function YearSelect({
  selectedYear,
  setSelectedYear,
  years,
  hasError = false,
}: {
  selectedYear: number;
  years: number[];
  setSelectedYear: (evt: SelectChangeEvent) => void;
  hasError?: boolean;
}) {
  return (
    <FormControl
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: "8px",
        alignItems: "center",
        width: "94px",
      }}
    >
      <InputLabel id="year-select-label">Year</InputLabel>
      <Select
        labelId="year-select-label"
        value={selectedYear.toString()}
        label="Year"
        onChange={setSelectedYear}
      >
        {years.map((year) => (
          <MenuItem key={year} value={year}>
            {year}
          </MenuItem>
        ))}
      </Select>
      {hasError && (
        <InfoOutlined sx={(theme) => ({ color: theme.palette.error.main })} />
      )}
    </FormControl>
  );
}

function Navigation({
  initiatives,
  activeInitiative,
  initiativesLockDetails,
  currentUser,
  switchReportingLock,
}: {
  initiatives: Initiative[];
  activeInitiative: Initiative;
  initiativesLockDetails: Record<number, LockDetail>;
  currentUser: User;
  switchReportingLock: (
    oldInitiativeId: number,
    newInitiativeId: number
  ) => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        height: "100%",
        background: "#DFEFF24F",
        padding: "16px",
      }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          width: "324px",
          padding: theme.spacing(1),
          overflowY: "auto",
        })}
      >
        {sortInitiatives(
          { key: "updateStatus", direction: "DESC" },
          initiatives.filter(
            (initiative) => initiative.updateStatus !== "ended"
          )
        ).map((initiative) => {
          const lockDetails = initiativesLockDetails?.[initiative.id];
          const isLockedByOtherUser =
            lockDetails?.isLocked &&
            lockDetails?.lockedBy !== currentUser.email;

          return (
            <Box
              sx={(theme) => ({
                borderLeft: `3px solid ${
                  activeInitiative.uid === initiative.uid
                    ? theme.palette.primary.main
                    : "#E2F1FA"
                }`,
                key: initiative.uid,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: theme.spacing(1),
                paddingY: theme.spacing(1.5),
              })}
            >
              <Tooltip
                componentsProps={{
                  tooltip: {
                    sx: (theme) => ({
                      bgcolor: "common.white",
                      color: "black",
                      border: "1px solid",
                      borderColor: theme.palette.secondary.main,
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: 400,
                      p: 1,
                    }),
                  },
                }}
                disableInteractive
                title={
                  isLockedByOtherUser
                    ? REPORTING_IMPACT_CURRENTLY_LOCKED_TOOLTIP.replace(
                        "$1",
                        lockDetails.lockedBy ?? "Unknown"
                      )
                    : ""
                }
              >
                <span>
                  {isLockedByOtherUser ? (
                    <Typography
                      noWrap
                      color="#122B54"
                      fontSize="16px"
                      lineHeight="20px"
                      sx={{
                        cursor: "pointer",
                        fontWeight:
                          activeInitiative.uid === initiative.uid ? 700 : 400,
                      }}
                    >
                      🔒 {initiative.initiativeName}
                    </Typography>
                  ) : (
                    <Link
                      style={{ maxWidth: "260px", textDecoration: "none" }}
                      to={generatePath(APP_ROUTES.impactUpdate, {
                        initiativeUid: initiative.uid,
                        viewname: "initiatives",
                        oeId: initiative.operatingEntity.id,
                      })}
                      onClick={() => {
                        switchReportingLock(activeInitiative.id, initiative.id);
                      }}
                    >
                      <Typography
                        noWrap
                        color="#122B54"
                        fontSize="16px"
                        lineHeight="20px"
                        sx={{
                          fontWeight:
                            activeInitiative.uid === initiative.uid ? 700 : 400,
                        }}
                      >
                        {initiative.initiativeName}
                      </Typography>
                    </Link>
                  )}
                </span>
              </Tooltip>
              <UpdateIcon status={initiative.updateStatus} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
